// src/components/Funnels/SkillToSaleQuizFunnel/SimulationResults.tsx
import React from "react";
import {
  CheckCircle,
  ArrowRight,
} from "lucide-react";
import { QuizData } from "./quizLogic";
import { useCreateOrUpdateRoadmapMutation } from "../../../app/api/roadmapApi";
export interface Strategy {
  difficulty: string;
  timeline: string;
  focus: string;
  color: string;
  confidence: string;
}
export interface Product {
  name: string;
  price: string;
  description: string;
  targetSales: string;
  timeline: string;
}
export interface ProductLadder {
  phase1: Product;
  phase2: Product;
  phase3: Product;
}
export interface RoadmapPhase {
  phase: number;
  title: string;
  timeline: string;
  revenue: number;
  keyActions: string[];
  milestone: string;
}
export interface Simulation {
  currentPotential: number;
  monthlyGoal: number;
  timeToGoal: number;
  strategy: Strategy;
  productLadder: ProductLadder;
  roadmapPhases: RoadmapPhase[];
  successProbability: number;
}

interface Props {
  simulation: Simulation;
  quizData: QuizData;
  onNameChange: (name: string) => void;
  onEmailChange: (email: string) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

const formatCurrency = (amount: number) => {
  if (amount >= 1000000) return `₦${(amount / 1000000).toFixed(1)}M`;
  return `₦${amount.toLocaleString()}`;
};

const SimulationResults: React.FC<Props> = ({
  simulation,
  quizData,
  onNameChange,
  onEmailChange,
  onSubmit,
  isSubmitting,
}) => {
  const [createOrUpdateRoadmap, { isLoading }] =
    useCreateOrUpdateRoadmapMutation();

  const handleFinalSubmit = async () => {
    // validate name & email
    if (!quizData.email || !quizData.name) {
      alert("Please enter name and email");
      return;
    }

    const payload = {
      email: quizData.email,
      name: quizData.name,
      quizData,
      simulation,
    };

    try {
      const resp = await createOrUpdateRoadmap(payload).unwrap();
      // resp.link contains the public link
      if (resp.link) {
        // open the personalised roadmap in a new tab (optional)
        window.open(resp.link, "_blank", "noopener");
      }
      // call parent onSubmit if existed
      if (onSubmit) onSubmit();
    } catch (err) {
      console.error(err);
      alert("Could not create roadmap. Try again.");
    }
  };

  return (
    <div className="min-h-screen bg-brandNavy flex items-center justify-center py-12 px-4">
      <div className="max-w-4xl w-full">
        <div className="text-center mb-6">
          <h1 className="text-3xl md:text-4xl font-extrabold text-brandWhite leading-tight">
            Your Million-Naira Roadmap
          </h1>
          <p className="mt-2 text-brandWhite/90">
            A concise, practical plan to monetize your{" "}
            <strong>{quizData.knowledge}</strong>.
          </p>
        </div>

        <div
          className="bg-white rounded-2xl p-6 shadow-xl border mb-6"
          style={{ borderColor: "#E9EDF3" }}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
            <div className="text-center">
              <div className="text-sm text-mutedGray">Current Potential</div>
              <div className="text-2xl font-bold text-brandNavy">
                {formatCurrency(simulation.currentPotential)}
              </div>
              <div className="text-xs text-mutedGray mt-1">per month</div>
            </div>

            <div className="text-center">
              <div className="text-sm text-mutedGray">Target Goal</div>
              <div className="text-2xl font-bold text-purple-700">
                {formatCurrency(simulation.monthlyGoal)}
              </div>
              <div className="text-xs text-mutedGray mt-1">target income</div>
            </div>

            <div className="text-center">
              <div className="text-sm text-mutedGray">Estimated Timeline</div>
              <div className="text-2xl font-bold text-green-600">
                {simulation.strategy.timeline}
              </div>
              <div className="text-xs text-mutedGray mt-1">to reach goal</div>
            </div>
          </div>
        </div>

        {/* Product ladder & roadmap & capture — as in the previous version */}
        <div
          className="bg-white rounded-2xl p-6 shadow-xl border mb-6"
          style={{ borderColor: "#E9EDF3" }}
        >
          <h3 className="text-xl font-semibold text-brandNavy mb-4">
            Three-Phase Product Ladder
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            {["phase1", "phase2", "phase3"].map((p, idx) => {
              const phase = (simulation.productLadder as any)[p];
              const accent =
                idx === 0
                  ? "bg-blue-50 text-blue-700"
                  : idx === 1
                  ? "bg-purple-50 text-purple-700"
                  : "bg-green-50 text-green-700";
              return (
                <div
                  key={p}
                  className="p-4 rounded-lg border"
                  style={{ borderColor: "#F1F5F9" }}
                >
                  <div
                    className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${accent} mb-3`}
                  >
                    Phase {idx + 1}
                  </div>
                  <div className="font-bold text-gray-800">{phase.name}</div>
                  <div className="text-sm font-semibold mt-1">
                    {phase.price}
                  </div>
                  <div className="text-sm text-mutedGray mt-2">
                    {phase.description}
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="text-xs text-mutedGray">
                      Target: {phase.targetSales}
                    </div>
                    <div className="text-xs text-mutedGray">
                      {phase.timeline}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div
          className="bg-white rounded-2xl p-6 shadow-xl border mb-8"
          style={{ borderColor: "#E9EDF3" }}
        >
          <h3 className="text-xl font-semibold text-brandNavy mb-4">
            Roadmap (Month-by-Month)
          </h3>
          <div className="space-y-3">
            {simulation.roadmapPhases.map((phase) => (
              <details
                key={phase.phase}
                className="rounded-lg p-3 border"
                style={{ borderColor: "#F1F5F9" }}
              >
                <summary className="cursor-pointer flex justify-between items-center font-semibold">
                  <span>
                    {phase.title} — {phase.timeline}
                  </span>
                  <span className="text-sm text-mutedGray">
                    {formatCurrency(phase.revenue)}
                  </span>
                </summary>

                <div className="mt-3 grid md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-semibold text-sm mb-2">Key Actions</h5>
                    <ul className="text-sm text-mutedGray space-y-1">
                      {phase.keyActions.map((a, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-1" />
                          <span>{a}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-brandLightNavy/5 p-3 rounded">
                    <h5 className="font-semibold text-sm mb-2">Milestone</h5>
                    <div className="text-sm font-semibold text-brandNavy">
                      {phase.milestone}
                    </div>
                  </div>
                </div>
              </details>
            ))}
          </div>

          <div className="mt-6 text-sm text-mutedGray mb-4">
            This is a simulated projection — use it as a tactical plan and
            starting point.
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              value={quizData.name}
              onChange={(e) => onNameChange(e.target.value)}
              className="w-full p-3 rounded-lg border"
              placeholder="Your full name"
              style={{ borderColor: "#E9EDF3" }}
            />
            <input
              value={quizData.email}
              onChange={(e) => onEmailChange(e.target.value)}
              className="w-full p-3 rounded-lg border"
              placeholder="Email address"
              style={{ borderColor: "#E9EDF3" }}
            />
            <button
              onClick={handleFinalSubmit}
              disabled={isLoading || isSubmitting}
              className="col-span-2 w-full inline-flex items-center justify-center gap-2 bg-brandRed text-brandWhite font-semibold px-4 py-3 rounded-lg hover:bg-brandRedHover transition"
            >
              {isLoading ? "Sending..." : "Get My Complete Roadmap"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="text-center text-sm text-brandWhite/80">
          <div className="inline-flex items-center gap-3">
            <CheckCircle className="w-4 h-4 text-green-400" />
            Secure • Private • No Spam
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimulationResults;
